import { createContext } from 'react';

export interface FetchMessagesPayload {
  data: {
    alternativeMessages: string[];
  }
}

export interface FetchAllMessagesPayload {
  data: {
    message: string;
    scenarioId: string;
  }[]
}

export interface FetchOptionsByMessagePayload {
  data: {
    message: string;
    options: string[];
  }
}

export interface FetchDynamicItemsPayload {
  data: {
    ids: {
      id: string;
      text?: string;
      itemUrl?: {
        id?: string;
        url?: string;
        source?: string;
        title?: string;
      }
    }[],
    tags: {
      id: string;
      name?: string;
    }[],
    categories: {
      id: string;
      name?: string;
      description?: string;
    }[],
  }
}

export interface FetchFilteredDynamicItemsPayload {
  data: {
    ids: {
      id: string;
      text?: string;
      itemUrl?: {
        id?: string;
        source?: string;
        title?: string;
      }
    }[],
    appliedFilters: {
      tags: string[];
      categories: string[];
    }
  }
}

export interface FetchAllScenarioPayload {
  data: {
    id: string;
    name: string;
  }[]
}

export interface FetchTaskManagementsPayload {
  data: {
    id: string;
    title: string;
    type: string;
    description: string;
  }[];
}

export interface FetchProfileManagementPayload {
  data: {
    type: string,
    nodeId: string;
    message: string;
    nodeType: string;
    sourceId: string;
  }[]
}

// LF360-1828: Onboarding Responses source. Unlike FetchProfileManagementPayload
// (a node-deletion-guard payload reused as-is by useFetchProfileFields.hook.ts),
// this is a purpose-built contract for onboarding questions - id is the
// onboarding question's own id (becomes the criterion's onboardingQuestionId),
// inputType is the source question's input type (same short vocabulary as
// profile fields: single|multi|input|picker|date|time), and options carries the
// answer options for choice types (single/multi/picker) directly, so the editor
// doesn't need the message-text-keyed fetchOptionsByMessage workaround Task 4
// (Participant Profile) had to use.
export interface FetchOnboardingQuestionsPayload {
  data: {
    id: string;
    title: string;
    inputType: string;
    options: string[];
  }[]
}

// LF360-2023: what a selected article id that is not in fetchArticles() points at.
// The portal backs it with POST /v1/pages/reference-states.
export type ArticleReferenceState =
  | 'available'
  | 'switched_off'
  | 'deleted'
  | 'other_project'
  | 'draft_row'
  | 'group_id';

export interface ArticleReference {
  id: string;
  title: string | null;
  state: ArticleReferenceState;
}

export interface FetchArticleReferencesPayload {
  data: ArticleReference[];
}

// LF360-2015: what the Suggestions picker shows when an article row is hovered.
// Display-ready plain text - the consumer strips markdown/html, the package
// renders no markup.
export interface ArticlePreview {
  id: string;
  title: string;
  excerpt?: string | null;
  imageUrl?: string | null;
  categories?: string[];
  tags?: string[];
}

export interface FetchArticlePreviewPayload {
  data: ArticlePreview;
}

// LF360-2030: what the consumer's own search hands back for a node's
// individually selected list - the ids it stores and the words to show.
export interface PickedItem {
  id: string;
  text: string;
}

export interface UploadImagePayload {
  data: {
    signedUrl: string;
    userUpload: {
      id: string;
      mimeType: string;
    }
  }
}

export interface UploadImageParams {
  base64: string;
  mimeType: string;
  sourceId: string;
  sourceType: string;
  cropMeta?: {width: number, height: number, x: number, y: number, unit: string};
}

export interface FetchImageUrlByIdPayload {
  data: Record<string, string>;
}

export interface FetchImageContentByIdPayload {
  data: Record<string, string>;
}

export interface FetchersContextValue {
  fetchAlternatives: (originalMessage: string, alreadyGeneratedMessages: string[]) => Promise<FetchMessagesPayload>;
  fetchAllMessages: () => Promise<FetchAllMessagesPayload>;
  fetchOptionsByMessage: (message: string) => Promise<FetchOptionsByMessagePayload>;
  fetchFitFacts: () => Promise<FetchDynamicItemsPayload>;
  fetchFilteredFitFacts: (tags: string[], categories: string[]) => Promise<FetchFilteredDynamicItemsPayload>;
  fetchArticles: () => Promise<FetchDynamicItemsPayload>;
  fetchFilteredArticles: (tags: string[], categories: string[]) => Promise<FetchFilteredDynamicItemsPayload>;
  // LF360-2023: optional; without it unavailable articles stay shown by id.
  fetchArticleReferences?: (ids: string[]) => Promise<FetchArticleReferencesPayload>;
  // LF360-2015: optional; without it the picker has no preview.
  fetchArticlePreview?: (id: string) => Promise<FetchArticlePreviewPayload>;
  // LF360-2030: optional; the consumer's search for a node's individually
  // selected articles / FitFacts. Given what is selected already, resolves with
  // what was added, or null when closed. Without it the list picker is used.
  pickArticles?: (selectedIds: string[]) => Promise<PickedItem[] | null>;
  pickFitFacts?: (selectedIds: string[]) => Promise<PickedItem[] | null>;
  fetchAllScenario: () => Promise<FetchAllScenarioPayload>;
  fetchTaskManagements: () => Promise<FetchTaskManagementsPayload>;
  fetchProfileManagement: () => Promise<FetchProfileManagementPayload>;
  // LF360-1828: Onboarding Responses source (see FetchOnboardingQuestionsPayload above).
  fetchOnboardingQuestions: () => Promise<FetchOnboardingQuestionsPayload>;
  uploadImage: (params: UploadImageParams) => Promise<UploadImagePayload>;
  fetchImageUrlById: (ids: string[], size?: string, withCrop?: boolean) => Promise<FetchImageUrlByIdPayload>;
  fetchImageContentById: (ids: string[], size?: string, withCrop?: boolean) => Promise<FetchImageContentByIdPayload>;
}
export const FetchContext = createContext<FetchersContextValue | undefined>(undefined);

interface FetchAlternativesContextProviderProps {
  children: React.ReactNode;
  fetchers: FetchersContextValue;
}
export const FetchProvider = ({ children, fetchers }: FetchAlternativesContextProviderProps) => {
  return (
    <FetchContext.Provider value={fetchers}>
      {children}
    </FetchContext.Provider>
  );
}
